<template>
    <section class="rounded-[28px] border border-gray-100 bg-white p-4 shadow-sm sm:p-6">
        <div class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
                <div class="flex flex-wrap items-center gap-2">
                    <h2 class="text-xl font-bold text-gray-800">ACIA</h2>
                    <span class="rounded-full bg-gradient-to-r from-cyan-600 to-cyan-400 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-white shadow-sm">
                        Premium
                    </span>
                </div>
                <p class="mt-1 text-sm text-gray-500">
                    Athlete Clinical Intelligence & Analytics: até 10 arquivos (exames, laudos, testes) cruzados pela IA em um relatório científico — com ou sem avaliação funcional.
                </p>
            </div>

            <button type="button" class="btn btn-sm acias-ai-cta flex gap-x-2" :disabled="starting" @click="openBuilder">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 mt-1">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456Z" />
                </svg>
                Nova ACIA
            </button>
        </div>

        <!-- Skeleton da listagem -->
        <div v-if="loadingList" class="mt-6 grid gap-4 lg:grid-cols-2">
            <div v-for="i in 2" :key="`acias-skeleton-${i}`" class="rounded-[24px] border border-slate-100 bg-slate-50/70 p-5">
                <div class="flex items-center justify-between">
                    <div class="skeleton h-4 w-40 rounded"></div>
                    <div class="skeleton h-6 w-20 rounded-full"></div>
                </div>
                <div class="mt-4 space-y-2">
                    <div class="skeleton h-3 w-full rounded"></div>
                    <div class="skeleton h-3 w-5/6 rounded"></div>
                    <div class="skeleton h-3 w-2/3 rounded"></div>
                </div>
                <div class="mt-4 flex gap-2">
                    <div class="skeleton h-6 w-24 rounded-full"></div>
                    <div class="skeleton h-6 w-28 rounded-full"></div>
                </div>
            </div>
        </div>

        <!-- Lista de ACIA geradas (preview — o conteúdo completo fica na página dedicada) -->
        <div v-else-if="visibleItems.length" class="mt-6 grid gap-4 lg:grid-cols-2">
            <template v-for="item in visibleItems" :key="item.uuid">
            <!-- Slot em andamento: a geração roda no servidor e o card se atualiza sozinho -->
            <AciasGeneratingCard
                v-if="item.status === 'generating'"
                :files-count="(item.files_used || []).length"
                :started-at="item.created_at"
            />

            <!-- Slot com falha: mantém o histórico visível e oferece nova tentativa -->
            <div v-else-if="item.status === 'failed'" class="rounded-[24px] border border-rose-200 bg-rose-50/70 p-5 shadow-sm">
                <div class="flex items-start justify-between gap-3">
                    <div class="min-w-0">
                        <div class="text-sm font-semibold text-rose-900">ACIA de {{ formatDate(item.created_at) }}</div>
                        <div class="mt-1 text-xs text-rose-400">{{ formatRelative(item.created_at) }}</div>
                    </div>
                    <span class="shrink-0 rounded-full bg-rose-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-rose-700">
                        Falhou
                    </span>
                </div>

                <p class="mt-3 text-sm leading-6 text-rose-900">{{ failureMessage(item) }}</p>

                <div class="mt-4 flex flex-wrap items-center gap-2 text-[11px] font-semibold text-rose-500">
                    <span class="rounded-full bg-white px-2.5 py-1 shadow-sm ring-1 ring-rose-200">
                        {{ (item.files_used || []).length }} documento(s)
                    </span>
                </div>

                <button
                    type="button"
                    class="btn btn-sm btn-outline mt-3 w-full"
                    :disabled="!canRetry(item) || starting"
                    @click="retry(item)"
                >
                    Tentar novamente
                </button>
            </div>

            <div
                v-else
                class="group cursor-pointer rounded-[24px] border border-slate-100 bg-slate-50/70 p-5 shadow-sm transition-colors hover:border-cyan-200 hover:bg-cyan-50/40"
                @click="goToDetail(item)"
            >
                <div class="flex items-start justify-between gap-3">
                    <div class="min-w-0">
                        <div class="text-sm font-semibold text-slate-800">ACIA de {{ formatDate(item.generated_at || item.created_at) }}</div>
                        <div class="mt-1 text-xs text-slate-400">{{ formatRelative(item.generated_at || item.created_at) }}</div>
                    </div>
                    <span
                        class="shrink-0 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em]"
                        :class="statusClass(item.status)"
                    >
                        {{ statusLabel(item.status) }}
                    </span>
                </div>

                <p v-if="item.executive_summary" class="mt-3 text-sm leading-6 text-slate-600 line-clamp-3">
                    {{ item.executive_summary }}
                </p>
                <p v-else-if="item.status === 'done'" class="mt-3 text-sm italic leading-6 text-slate-400">
                    Sem resumo executivo registrado — abra a página dedicada para ver o relatório.
                </p>

                <div class="mt-4 flex flex-wrap items-center gap-2 text-[11px] font-semibold text-slate-500">
                    <span class="rounded-full bg-white px-2.5 py-1 shadow-sm ring-1 ring-slate-200">
                        {{ (item.files_used || []).length }} documento(s)
                    </span>
                    <span v-if="item.key_correlations_count" class="rounded-full bg-white px-2.5 py-1 shadow-sm ring-1 ring-slate-200">
                        {{ item.key_correlations_count }} cruzamentos
                    </span>
                    <span class="ml-auto inline-flex items-center gap-1 text-cyan-600 opacity-0 transition-opacity group-hover:opacity-100">
                        Ver completa
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="h-3.5 w-3.5">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                        </svg>
                    </span>
                </div>

                <button
                    v-if="item.status === 'done'"
                    type="button"
                    class="btn btn-sm btn-primary mt-3 w-full"
                    :disabled="exportingUuid === item.uuid"
                    @click.stop="exportPdf(item)"
                >
                    {{ exportingUuid === item.uuid ? 'Exportando...' : 'Exportar PDF' }}
                </button>
            </div>
            </template>
        </div>

        <!-- Modal builder: 1 avaliação + arquivos -->
        <transition name="fade">
            <div v-if="builderOpen" class="fixed inset-0 z-[60] flex items-end justify-center bg-slate-950/45 backdrop-blur-[2px] sm:items-center sm:px-6">
                <div class="flex max-h-[94vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-[28px] bg-white shadow-2xl sm:rounded-[28px]">
                    <div class="flex items-start justify-between gap-4 border-b border-slate-100 px-4 py-5 sm:px-6">
                        <div>
                            <h3 class="text-lg font-bold text-slate-800">Nova ACIA</h3>
                            <p class="mt-1 text-sm text-slate-500">Escolha a base da análise e selecione até 10 arquivos para cruzar.</p>
                        </div>
                        <button type="button" class="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 text-slate-500 hover:bg-slate-50" @click="closeBuilder">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="h-5 w-5">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <div class="flex-1 space-y-6 overflow-y-auto px-4 py-5 sm:px-6">
                        <!-- Passo 1: avaliação -->
                        <div>
                            <div class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Passo 1 — Base da análise</div>

                            <div v-if="loadingRecords" class="mt-3 space-y-2">
                                <div v-for="i in 2" :key="`rec-skel-${i}`" class="skeleton h-14 w-full rounded-2xl"></div>
                            </div>

                            <div v-else class="mt-3 space-y-2">
                                <label
                                    v-for="record in builderRecords"
                                    :key="record.uuid"
                                    class="flex cursor-pointer items-center gap-3 rounded-2xl border px-4 py-3 transition-colors"
                                    :class="selectedRecordUuid === record.uuid ? 'border-cyan-300 bg-cyan-50/60' : 'border-slate-200 hover:border-cyan-200'"
                                >
                                    <input
                                        type="radio"
                                        class="radio radio-sm radio-primary"
                                        :value="record.uuid"
                                        v-model="selectedRecordUuid"
                                    />
                                    <div class="min-w-0">
                                        <div class="text-sm font-semibold text-slate-700">Avaliação de {{ formatDate(record.created_at) }}</div>
                                        <div class="text-xs text-slate-400">
                                            {{ record.matrix_uuid ? 'Matriz vinculada' : 'Sem matriz de risco' }} · {{ formatRelative(record.created_at) }}
                                        </div>
                                    </div>
                                </label>

                                <label
                                    class="flex cursor-pointer items-center gap-3 rounded-2xl border px-4 py-3 transition-colors"
                                    :class="documentsOnly ? 'border-cyan-300 bg-cyan-50/60' : 'border-slate-200 hover:border-cyan-200'"
                                >
                                    <input
                                        type="radio"
                                        class="radio radio-sm radio-primary"
                                        :value="DOCUMENTS_ONLY"
                                        v-model="selectedRecordUuid"
                                    />
                                    <div class="min-w-0">
                                        <div class="text-sm font-semibold text-slate-700">Somente documentos</div>
                                        <div class="text-xs text-slate-400">
                                            Sem avaliação funcional — a IA cruza os documentos entre si e com a anamnese do paciente.
                                        </div>
                                    </div>
                                </label>
                            </div>
                        </div>

                        <!-- Passo 2: arquivos -->
                        <div>
                            <div class="flex flex-wrap items-center justify-between gap-2">
                                <div class="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">Passo 2 — Arquivos</div>
                                <span
                                    class="rounded-full px-2.5 py-1 text-[11px] font-semibold"
                                    :class="selectedFileIds.length ? 'bg-cyan-50 text-cyan-700' : 'bg-slate-100 text-slate-400'"
                                >
                                    {{ selectedFileIds.length }}/{{ maxFiles }} selecionado(s)
                                </span>
                            </div>

                            <div v-if="!supportedFiles.length" class="mt-3 rounded-2xl border border-dashed border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-800">
                                A ACIA precisa de pelo menos <strong>1 arquivo vinculado ao paciente</strong> (PDF ou imagem).
                                Envie exames e laudos na seção <strong>Arquivos</strong> do prontuário e volte aqui.
                            </div>

                            <template v-else>
                                <div class="relative mt-3">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                                    </svg>
                                    <input
                                        v-model="fileSearch"
                                        type="text"
                                        placeholder="Buscar arquivo por nome..."
                                        class="input input-bordered w-full rounded-2xl pl-11 text-sm"
                                    />
                                </div>

                                <div class="mt-3 max-h-64 space-y-2 overflow-y-auto pr-1">
                                    <label
                                        v-for="file in filteredFiles"
                                        :key="file.id"
                                        class="flex cursor-pointer items-center gap-3 rounded-2xl border px-4 py-3 transition-colors"
                                        :class="selectedFileIds.includes(file.id)
                                            ? 'border-cyan-300 bg-cyan-50/60'
                                            : isSelectable(file)
                                                ? 'border-slate-200 hover:border-cyan-200'
                                                : 'cursor-not-allowed border-slate-100 opacity-50'"
                                    >
                                        <input
                                            type="checkbox"
                                            class="checkbox checkbox-sm checkbox-primary"
                                            :checked="selectedFileIds.includes(file.id)"
                                            :disabled="!isSelectable(file)"
                                            @change="toggleFile(file)"
                                        />
                                        <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-[10px] font-bold uppercase text-slate-500">
                                            {{ fileExtension(file) }}
                                        </div>
                                        <div class="min-w-0 flex-1">
                                            <div class="truncate text-sm font-semibold text-slate-700">{{ file.exam_description || file.description }}</div>
                                            <div class="text-xs text-slate-400">{{ formatDate(file.created_at) }}</div>
                                        </div>
                                    </label>

                                    <div v-if="!filteredFiles.length" class="rounded-2xl bg-slate-50 px-4 py-4 text-sm text-slate-400">
                                        Nenhum arquivo encontrado para "{{ fileSearch }}".
                                    </div>
                                </div>

                                <p v-if="!selectedFileIds.length" class="mt-3 text-xs font-semibold text-amber-600">
                                    Selecione pelo menos 1 arquivo — a ACIA existe para analisar documentos.
                                </p>
                                <p v-else-if="selectedFileIds.length >= maxFiles" class="mt-3 text-xs font-semibold text-slate-500">
                                    Limite de {{ maxFiles }} arquivos por análise atingido.
                                </p>
                            </template>
                        </div>
                    </div>

                    <div class="flex flex-col gap-2 border-t border-slate-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
                        <div class="text-xs text-slate-400">{{ documentsOnly ? 'Somente documentos' : '1 avaliação' }} + {{ selectedFileIds.length }} arquivo(s) selecionado(s)</div>
                        <div class="flex gap-2">
                            <button type="button" class="btn btn-outline btn-sm flex-1 sm:flex-none" @click="closeBuilder">Cancelar</button>
                            <button
                                type="button"
                                class="btn btn-primary btn-sm flex-1 sm:flex-none"
                                :disabled="!canGenerate"
                                @click="generate"
                            >
                                {{ starting ? 'Enviando...' : 'Criar ACIA' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </section>
</template>

<script>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import Swal from 'sweetalert';
import AciasGeneratingCard from '@/components/AciasGeneratingCard.vue';
import { AciasService, RecordService, UsageService } from '@/services/Entity';
import { PLANS } from '@/constants/plans';

const MAX_FILES = 10;

// Valor do radio "Somente documentos" — vira record_uuid null na API
const DOCUMENTS_ONLY = 'documents_only';

// A geração roda no servidor: o slot em andamento é atualizado por polling da listagem
const POLL_INTERVAL_MS = 6000;

const FAILURE_MESSAGES = {
    timeout:                   'A geração passou do tempo limite e foi interrompida. Tente novamente.',
    no_valid_files:            'Os arquivos selecionados não puderam ser lidos. Reenvie os documentos e tente de novo.',
    openrouter_not_configured: 'A integração com a IA não está configurada neste ambiente.',
    openrouter_failed:         'A IA não respondeu a tempo. Tente novamente em instantes.'
};

const SUPPORTED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

function normalizeMimeType(type) {
    return String(type || '')
        .split(';')[0]
        .trim()
        .toLowerCase();
}

export default {
    name: 'AciasSection',
    components: {
        AciasGeneratingCard
    },
    props: {
        patient: {
            type: Object,
            required: true
        }
    },
    setup(props) {
        const router = useRouter();
        const loadingList = ref(true);
        const items = ref([]);
        const builderOpen = ref(false);
        const loadingRecords = ref(false);
        const builderRecords = ref([]);
        const selectedRecordUuid = ref('');
        const selectedFileIds = ref([]);
        const fileSearch = ref('');
        const starting = ref(false);
        const exportingUuid = ref('');
        const highestPlanId = PLANS[PLANS.length - 1]?.id || 'standard';

        const supportedFiles = computed(() => {
            return (props.patient?.files || []).filter((file) => {
                return SUPPORTED_TYPES.includes(normalizeMimeType(file && file.type));
            });
        });

        const visibleItems = computed(() => {
            return (items.value || []).filter(Boolean);
        });

        const filteredFiles = computed(() => {
            const query = fileSearch.value.trim().toLowerCase();
            const sorted = [...supportedFiles.value].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            if (!query) return sorted;

            return sorted.filter((file) => {
                const name = `${file.exam_description || ''} ${file.description || ''}`.toLowerCase();
                return name.includes(query);
            });
        });

        const documentsOnly = computed(() => selectedRecordUuid.value === DOCUMENTS_ONLY);

        const canGenerate = computed(() => {
            return Boolean(selectedRecordUuid.value)
                && selectedFileIds.value.length >= 1
                && selectedFileIds.value.length <= MAX_FILES
                && !starting.value;
        });

        watch(() => props.patient?.uuid, (uuid) => {
            if (uuid) loadList(uuid);
        }, { immediate: true });

        let pollTimer = null;

        async function loadList(patientUuid, options = {}) {
            const silent = Boolean(options.silent);

            try {
                if (!silent) loadingList.value = true;
                const { data } = await AciasService.listByPatient(patientUuid);
                items.value = (data && data.data) || [];
            } catch (err) {
                // Falha de rede no polling não pode limpar o que já está na tela
                if (!silent) items.value = [];
            } finally {
                if (!silent) loadingList.value = false;
                syncPolling();
            }
        }

        // Liga o polling enquanto houver slot em andamento e desliga quando não houver
        function syncPolling() {
            const generating = (items.value || []).some((item) => item && item.status === 'generating');

            if (generating && !pollTimer) {
                pollTimer = setInterval(() => {
                    if (props.patient?.uuid) loadList(props.patient.uuid, { silent: true });
                }, POLL_INTERVAL_MS);
                return;
            }

            if (!generating && pollTimer) {
                clearInterval(pollTimer);
                pollTimer = null;
            }
        }

        onBeforeUnmount(() => {
            if (pollTimer) clearInterval(pollTimer);
        });

        async function openBuilder() {
            builderOpen.value = true;
            fileSearch.value = '';
            selectedFileIds.value = [];

            try {
                loadingRecords.value = true;
                const { data: { items: records } } = await RecordService.listByPatientUUID(1, 50, props.patient.uuid);
                builderRecords.value = records || [];
                selectedRecordUuid.value = builderRecords.value[0]?.uuid || DOCUMENTS_ONLY;
            } catch (err) {
                builderRecords.value = [];
                selectedRecordUuid.value = DOCUMENTS_ONLY;
            } finally {
                loadingRecords.value = false;
            }
        }

        function closeBuilder() {
            if (starting.value) return;
            builderOpen.value = false;
        }

        function isSelectable(file) {
            if (selectedFileIds.value.includes(file.id)) return true;
            return selectedFileIds.value.length < MAX_FILES;
        }

        function toggleFile(file) {
            const index = selectedFileIds.value.indexOf(file.id);
            if (index >= 0) {
                selectedFileIds.value.splice(index, 1);
                return;
            }
            if (selectedFileIds.value.length >= MAX_FILES) return;
            selectedFileIds.value.push(file.id);
        }

        function fileExtension(file) {
            const name = file.description || file.filename || '';
            if (name.indexOf('.') === -1) return 'file';
            return name.split('.').pop().slice(0, 4);
        }

        async function showUsageLimitModal() {
            let usage = null;
            try {
                const { data } = await UsageService.getUsage();
                usage = (data && data.data) || null;
            } catch (err) { /* segue com modal genérico */ }

            if (usage && usage.plan && usage.plan !== highestPlanId) {
                const shouldUpgrade = await Swal({
                    title: 'Limite de ACIA atingido',
                    text: 'Seu plano atual não possui mais créditos de ACIA neste ciclo. Faça upgrade para continuar cruzando exames com IA.',
                    icon: 'warning',
                    buttons: {
                        cancel: 'Agora não',
                        confirm: {
                            text: 'Ver planos',
                            value: true
                        }
                    }
                });

                if (shouldUpgrade) {
                    router.push({ name: 'subscription' });
                }
                return;
            }

            await Swal({
                title: 'Limite mensal de ACIA atingido',
                text: 'Você já está no melhor plano e os créditos de ACIA deste ciclo foram consumidos. Eles renovam no próximo mês.',
                icon: 'warning'
            });
        }

        async function generate() {
            if (!canGenerate.value) return;
            const recordUuid = documentsOnly.value ? null : selectedRecordUuid.value;
            await startGeneration(recordUuid, [...selectedFileIds.value]);
        }

        function canRetry(item) {
            return (item?.files_used || []).some((file) => file && file.id);
        }

        async function retry(item) {
            if (!canRetry(item) || starting.value) return;
            const fileIds = (item.files_used || []).map((file) => file.id).filter(Boolean);
            await startGeneration(item.record_uuid || null, fileIds);
        }

        function failureMessage(item) {
            return FAILURE_MESSAGES[item?.error_code] || 'A geração falhou antes de concluir. Tente novamente.';
        }

        // Só dispara a geração: o servidor devolve o registro em 'generating' e o slot
        // do prontuário acompanha o resto por polling — nada de esperar a IA aqui.
        async function startGeneration(recordUuid, fileIds) {
            try {
                starting.value = true;
                await AciasService.generate(recordUuid, fileIds, props.patient.uuid);

                builderOpen.value = false;
                await loadList(props.patient.uuid, { silent: true });
            } catch (err) {
                const errorCode = err?.response?.data?.error;

                if (errorCode === 'usage_limit_reached') {
                    await showUsageLimitModal();
                    return;
                }

                if (errorCode === 'openrouter_not_configured') {
                    await Swal({
                        title: 'IA indisponível',
                        text: 'A integração com OpenRouter ainda não está configurada neste ambiente.',
                        icon: 'warning'
                    });
                    return;
                }

                if (errorCode === 'no_valid_files' || errorCode === 'missing_files') {
                    await Swal({
                        title: 'Arquivos inválidos',
                        text: 'Selecione pelo menos 1 arquivo PDF ou imagem vinculado ao paciente.',
                        icon: 'warning'
                    });
                    return;
                }

                await Swal({
                    title: 'Falha ao gerar a ACIA',
                    text: 'Não foi possível iniciar a geração agora. Tente mais tarde.',
                    icon: 'warning'
                });
            } finally {
                starting.value = false;
            }
        }

        async function exportPdf(item) {
            if (!item?.uuid) return;

            try {
                exportingUuid.value = item.uuid;
                const { data } = await AciasService.exportPdf(item.uuid);
                const pdfUrl = data?.data?.pdf_url;

                if (pdfUrl) {
                    window.open(pdfUrl, '_blank', 'noopener,noreferrer');
                }
            } catch (err) {
                await Swal({
                    title: 'Ocorreu um problema ao exportar o PDF',
                    text: 'Tente novamente em instantes. Se o erro persistir, entre em contato com o suporte.',
                    icon: 'error'
                });
            } finally {
                exportingUuid.value = '';
            }
        }

        function goToDetail(item) {
            if (!item?.uuid) return;
            router.push({
                name: 'aciasDetail',
                params: { patient_uuid: props.patient.uuid, acias_uuid: item.uuid }
            });
        }

        function statusLabel(status) {
            if (status === 'done') return 'Concluída';
            if (status === 'generating') return 'Gerando';
            return 'Falhou';
        }

        function statusClass(status) {
            if (status === 'done') return 'bg-emerald-50 text-emerald-700';
            if (status === 'generating') return 'bg-cyan-50 text-cyan-700';
            return 'bg-rose-50 text-rose-700';
        }

        function formatDate(dateStr) {
            if (!dateStr) return '—';
            const date = new Date(dateStr);
            if (Number.isNaN(date.getTime())) return '—';
            return date.toLocaleDateString('pt-BR');
        }

        function formatRelative(dateStr) {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            if (Number.isNaN(date.getTime())) return '';

            const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
            if (diffSec < 60) return 'agora';
            const diffMin = Math.floor(diffSec / 60);
            if (diffMin < 60) return `há ${diffMin} min`;
            const diffHour = Math.floor(diffMin / 60);
            if (diffHour < 24) return `há ${diffHour}h`;
            const diffDay = Math.floor(diffHour / 24);
            if (diffDay < 30) return `há ${diffDay} ${diffDay === 1 ? 'dia' : 'dias'}`;
            const diffMonth = Math.floor(diffDay / 30);
            if (diffMonth < 12) return `há ${diffMonth} ${diffMonth === 1 ? 'mês' : 'meses'}`;
            const diffYear = Math.floor(diffMonth / 12);
            return `há ${diffYear} ${diffYear === 1 ? 'ano' : 'anos'}`;
        }

        return {
            loadingList,
            items,
            builderOpen,
            loadingRecords,
            builderRecords,
            selectedRecordUuid,
            documentsOnly,
            DOCUMENTS_ONLY,
            selectedFileIds,
            fileSearch,
            starting,
            exportingUuid,
            exportPdf,
            supportedFiles,
            filteredFiles,
            visibleItems,
            canGenerate,
            maxFiles: MAX_FILES,
            openBuilder,
            closeBuilder,
            isSelectable,
            toggleFile,
            fileExtension,
            generate,
            retry,
            canRetry,
            failureMessage,
            goToDetail,
            statusLabel,
            statusClass,
            formatDate,
            formatRelative
        };
    }
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.acias-ai-cta {
    position: relative;
    overflow: hidden;
    border-color: rgba(8, 145, 178, 0.55) !important;
    background: linear-gradient(135deg, #0891b2 0%, #06b6d4 45%, #0e7490 100%) !important;
    color: #ffffff !important;
    box-shadow: 0 4px 10px rgba(8, 145, 178, 0.16);
}

.acias-ai-cta::before {
    content: '';
    position: absolute;
    top: 0;
    left: 100%;
    width: 60%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.45), transparent);
    animation: acias-ai-shine 2s linear infinite;
}

.acias-ai-cta:hover {
    border-color: rgba(14, 116, 144, 1) !important;
    box-shadow: 0 0 0 3px rgba(34, 211, 238, 0.14), 0 6px 14px rgba(8, 145, 178, 0.22);
}

.acias-ai-cta:disabled::before,
.acias-ai-cta:disabled {
    animation: none;
}

.acias-ai-cta:disabled::before {
    display: none;
}

@keyframes acias-ai-shine {
    0% { left: 100%; }
    100% { left: -60%; }
}
</style>
